import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Segmentation } from '@segmentation/Segmentation.tsx';
import Correction from '@correction/Correction.tsx';
import { useEffect, useState } from 'react';
import { PatientsMenu } from '@patientMenu/PatientsMenu.tsx';
import Export from '@export/Export.tsx';
import GeneralSettings from '@/modules/settings/GeneralSettings.tsx';
import TermsOfUsage from '@/modules/settings/TermsOfUsage.tsx';
import { Login } from './modules/Login/Login.tsx';
import { ImportScan } from '@modules/importScan/ImportScan.tsx';
import { Toaster } from '@/components/ui/sonner.tsx';
import initAuth from '@/auth/initAuth.ts';
import { ProtectedRoute } from '@/route/ProtectedRoute.tsx';
import AddPatient from '@patientMenu/features/AddPatient.tsx';
import { EditPatient } from '@patientMenu/features/EditPatient.tsx';
import { RemoveScan } from '@patientMenu/features/RemoveScan.tsx';
import EditNotes from '@patientMenu/features/EditNotes.tsx';
import RemovePatient from '@patientMenu/features/RemovePatient.tsx';
import { DevRoom } from '@bracesDesigner/devRoom/DevRoom.tsx';
import TreatmentPlanPage from '@modules/treatmentPlan/TreatmentPlanPage.tsx';
import { RouteError404 } from '@modules/errors/RouteError404.tsx';
import AppLayout from '@/layout/AppLayout.tsx';
import ThreeSceneLayout from '@/layout/ThreeSceneLayout.tsx';
import AdminLayout from '@modules/adminPanel/AdminLayout.tsx';
import AdminOverview from '@modules/adminPanel/pages/AdminOverview.tsx';
import AdminClinics from '@modules/adminPanel/pages/AdminClinics.tsx';
import AdminClinicDetail from '@modules/adminPanel/pages/AdminClinicDetail.tsx';
import AdminPipeline from '@modules/adminPanel/pages/AdminPipeline.tsx';
import AdminPeople from '@modules/adminPanel/pages/AdminPeople.tsx';
import AdminInvitations from '@modules/adminPanel/pages/AdminInvitations.tsx';
import AdminIssues from '@modules/adminPanel/pages/AdminIssues.tsx';
import AdminIssueDetail from '@modules/adminPanel/pages/AdminIssueDetail.tsx';
import AdminAuditLog from '@modules/adminPanel/pages/AdminAuditLog.tsx';
import AcceptInvite from '@modules/invite/AcceptInvite.tsx';
import { AdminRoute } from '@/route/AdminRoute.tsx';
import { ClinicRoute } from '@/route/ClinicRoute.tsx';
import { ClinicWorkflowRoute } from '@/route/ClinicWorkflowRoute.tsx';
import { HomeRedirect } from '@/route/HomeRedirect.tsx';
import { ReportProblemDialog } from '@modules/support/ReportProblemDialog.tsx';
import { AppErrorBoundary } from '@modules/support/AppErrorBoundary.tsx';

const queryClient = new QueryClient();

function App() {
  const [authReady, setAuthReady] = useState(false);
  const location = useLocation();
  const background = location.state?.backgroundLocation;

  useEffect(() => {
    initAuth().finally(() => {
      setAuthReady(true);
    });
  }, []);

  if (!authReady) {
    return (
      <></>
    );
  }

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <AppErrorBoundary>
          <Routes location={background || location}>
            {/* Accounts now come from an admin invitation, so self-registration is gone. */}
            <Route path={'/register'} element={<Navigate to="/login" replace />} />
            <Route path={'/invite/:token'} element={<AcceptInvite />} />
            <Route path={'/login'} element={<Login />} />
            <Route path={'/login/reset-password'} element={<Login />} />
            <Route path={'/dev-room'} element={<DevRoom />} />
            <Route element={<ProtectedRoute />}>
              {/* Admins get their own viewport; everyone else lands in the clinic app. */}
              <Route index element={<HomeRedirect />} />
              <Route element={<AdminRoute />}>
                <Route path={'admin'} element={<AdminLayout />}>
                  <Route index element={<AdminOverview />} />
                  <Route path={'clinics'} element={<AdminClinics />} />
                  <Route path={'clinics/:clinicId'} element={<AdminClinicDetail />} />
                  <Route path={'pipeline'} element={<AdminPipeline />} />
                  <Route path={'people'} element={<AdminPeople />} />
                  <Route path={'invitations'} element={<AdminInvitations />} />
                  <Route path={'issues'} element={<AdminIssues />} />
                  <Route path={'issues/:issueId'} element={<AdminIssueDetail />} />
                  <Route path={'audit-log'} element={<AdminAuditLog />} />
                </Route>
              </Route>
              {/* Account-level screens belong to whoever is signed in, admin included. */}
              <Route element={<AppLayout />}>
                <Route path={'settings/general'} element={<GeneralSettings />} />
                <Route path={'settings/terms'} element={<TermsOfUsage />} />
              </Route>
              <Route element={<ClinicRoute />}>
                <Route element={<AppLayout />}>
                  <Route path={'patients'} element={<PatientsMenu />} />
                  <Route path={'patients/:patientId'} element={<PatientsMenu />} />
                  <Route path={'patients/:patientId/scans/:scanId'} element={<PatientsMenu />} />
                </Route>
                <Route element={<ThreeSceneLayout />}>
                  <Route path={'import-scan/:patientId'} element={<ImportScan />} />
                </Route>
              </Route>
              {/* The clinical tools themselves: a superadmin opens these on any case
                  straight from the console, so they sit behind a laxer guard. */}
              <Route element={<ClinicWorkflowRoute />}>
                <Route element={<AppLayout />}>
                  <Route path={'treatment-plan/:scanId'} element={<TreatmentPlanPage />} />
                </Route>
                <Route element={<ThreeSceneLayout />}>
                  <Route path={'patients/:patientId/scans/:scanId/segmentation'} element={<Segmentation />} />
                  <Route path={'patients/:patientId/scans/:scanId/correction'} element={<Correction />} />
                  <Route path={'patients/:patientId/scans/:scanId/export'} element={<Export />} />
                </Route>
              </Route>
            </Route>
            <Route path={'*'} element={<RouteError404 />} />
          </Routes>
        </AppErrorBoundary>
        {background && (
          <Routes>
            <Route path={'/patients/add'} element={<AddPatient onClose={() => window.history.back()} />} />
            <Route path={'/patients/:patientId/edit'} element={<EditPatient onClose={() => window.history.back()} />} />
            <Route path={'/patients/:patientId/scans/:scanId/delete'} element={<RemoveScan />} />
            <Route path={'/patients/:patientId/scans/:scanId/note/edit'} element={<EditNotes onClose={() => window.history.back()} />} />
            <Route path={'/patients/delete'} element={<RemovePatient />} />
          </Routes>
        )}
        {/* Mounted once so any screen can open it through useReportProblemStore. */}
        <ReportProblemDialog />
      </QueryClientProvider>
      <Toaster />
    </>
  );
}

export default App;
